// features/inventory/pages/ReorderSuggestionsPage.tsx
/**
 * Reorder Suggestions Page — Products below reorder point with auto-requisition creation.
 *
 * Route: /inventory/reorder
 *
 * Features:
 * - Products below reorder point grouped by supplier
 * - Summary cards: products needing reorder, total estimated cost, pending requisitions
 * - Grouped by vendor/supplier with expandable sections
 * - Individual product rows with current stock, reorder point, suggested quantity, estimated cost
 * - Bulk "Create Requisitions" action using autoRequisition endpoint
 * - Branch filter
 * - Visual urgency indicators (red = out of stock, amber = below reorder point)
 * - Export reorder list to Excel
 * - Pagination for supplier groups
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Pagination } from '@/components/ui/Pagination';
import { getStockLevels } from '@/features/stock/services/stockApi';
import { getProducts, ProductData } from '@/features/products/services/productsApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft,
    ShoppingCart,
    Package,
    Store,
    TrendingDown,
    AlertTriangle,
    DollarSign,
    RefreshCw,
    Download,
    ChevronDown,
    ChevronUp,
    Send,
    X,
    Search,
    Filter,
    XCircle,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

interface ReorderItem {
    productId: string;
    productName: string;
    sku: string;
    currentStock: number;
    reorderPoint: number;
    reorderQuantity: number;
    costPrice: number;
    estimatedCost: number;
    supplierId: string | null;
    supplierName: string;
    branchId: string;
    branchName: string;
    urgency: 'critical' | 'low' | 'normal';
}

interface SupplierGroup {
    supplierId: string;
    supplierName: string;
    items: ReorderItem[];
    totalItems: number;
    totalEstimatedCost: number;
}

interface AutoRequisitionResponse {
    requisitionsCreated: number;
    productsRequested: number;
    skippedCount: number;
}

// ============================================================
// CONSTANTS
// ============================================================

const NIL_UUID = '00000000-0000-0000-0000-000000000000';
const PAGE_SIZE = 10; // Number of supplier groups per page

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ReorderSuggestionsPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // State
    const [branchId, setBranchId] = useState(selectedBranchId || '');
    const [search, setSearch] = useState('');
    const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set());
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);

    // Queries
    const { data: branches } = useQuery({
        queryKey: ['branches-reorder', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    // Fetch stock levels
    const { data: stockResponse, isLoading: stockLoading } = useQuery({
        queryKey: ['stock-levels-reorder', user?.tenantId, branchId],
        queryFn: async () => {
            const response = await getStockLevels(user!.tenantId, {
                limit: 500,
                branchId: branchId || undefined,
                sortBy: 'quantityOnHand',
                order: 'asc',
            });
            return response?.data || [];
        },
        enabled: !!user?.tenantId,
    });

    // Fetch all products for supplier/cost info
    const { data: productsResponse } = useQuery({
        queryKey: ['products-reorder', user?.tenantId],
        queryFn: async () => {
            const response = await getProducts(user!.tenantId, {
                limit: 500,
                sortBy: 'name',
                order: 'asc',
            });
            return response?.data || [];
        },
        enabled: !!user?.tenantId,
    });

    // Mutation for auto-requisition
    const autoRequisitionMutation = useMutation({
        mutationFn: async () => {
            const response = await apiClient.post(
                `/tenants/${user!.tenantId}/purchase-requisitions/auto`,
                {},
                { params: { branchId: branchId || undefined } }
            );
            return response.data?.data as AutoRequisitionResponse;
        },
        onSuccess: (result) => {
            setShowConfirmModal(false);
            addToast({
                type: 'success',
                title: 'Requisitions Created',
                message: `${result.requisitionsCreated} requisitions with ${result.productsRequested} products.`,
            });
            queryClient.invalidateQueries({
                queryKey: ['stock-levels-reorder'],
            });
        },
        onError: (err: any) => {
            setShowConfirmModal(false);
            addToast({
                type: 'error',
                title: 'Failed to create requisitions',
                message: err?.response?.data?.error?.message || err?.message,
            });
        },
    });

    // Derived data
    const branchesList = (branches || []) as Array<{ id: string; name: string }>;
    const stockItems = Array.isArray(stockResponse) ? stockResponse : [];
    const products = Array.isArray(productsResponse) ? productsResponse : [];

    // Build product lookup map
    const productMap = useMemo(() => {
        const map = new Map<string, ProductData>();
        products.forEach((p) => map.set(p.id, p));
        return map;
    }, [products]);

    // Build reorder items list
    const reorderItems: ReorderItem[] = useMemo(() => {
        return stockItems
            .filter((item: any) => {
                const onHand = parseFloat(item.quantityOnHand || '0');
                const threshold = item.lowStockThreshold || 10;
                const reorderPoint = item.reorderPoint || threshold;
                return onHand <= reorderPoint;
            })
            .map((item: any) => {
                const product = productMap.get(item.productId);
                const onHand = parseFloat(item.quantityOnHand || '0');
                const reorderPoint = item.reorderPoint || item.lowStockThreshold || 10;
                const reorderQty = item.reorderQuantity || Math.max(10, reorderPoint * 2);
                const costPrice = parseFloat(product?.costPrice || '0');
                const estimatedCost = costPrice * reorderQty;

                let urgency: ReorderItem['urgency'] = 'normal';
                if (onHand <= 0) urgency = 'critical';
                else if (onHand <= reorderPoint / 2) urgency = 'low';

                return {
                    productId: item.productId,
                    productName: item.productName || product?.name || 'Unknown',
                    sku: product?.sku || '',
                    currentStock: onHand,
                    reorderPoint,
                    reorderQuantity: reorderQty,
                    costPrice,
                    estimatedCost,
                    supplierId: product?.supplierId || null,
                    supplierName: product?.supplierName || 'No Supplier',
                    branchId: item.branchId,
                    branchName: item.branchName || 'Warehouse',
                    urgency,
                };
            });
    }, [stockItems, productMap]);

    // Filter by search
    const filteredItems = useMemo(() => {
        if (!search) return reorderItems;
        const q = search.toLowerCase();
        return reorderItems.filter(
            (item) =>
                item.productName.toLowerCase().includes(q) ||
                item.sku.toLowerCase().includes(q) ||
                item.supplierName.toLowerCase().includes(q)
        );
    }, [reorderItems, search]);

    // Group by supplier
    const supplierGroups: SupplierGroup[] = useMemo(() => {
        const groupMap = new Map<string, SupplierGroup>();

        filteredItems.forEach((item) => {
            const key = item.supplierId || 'no-supplier';
            const existing = groupMap.get(key);
            if (existing) {
                existing.items.push(item);
                existing.totalItems++;
                existing.totalEstimatedCost += item.estimatedCost;
            } else {
                groupMap.set(key, {
                    supplierId: key,
                    supplierName: item.supplierName,
                    items: [item],
                    totalItems: 1,
                    totalEstimatedCost: item.estimatedCost,
                });
            }
        });

        return Array.from(groupMap.values()).sort((a, b) =>
            a.supplierName.localeCompare(b.supplierName)
        );
    }, [filteredItems]);

    // Pagination for supplier groups
    const totalSupplierGroups = supplierGroups.length;
    const totalPages = Math.max(1, Math.ceil(totalSupplierGroups / pageSize));
    const paginatedSupplierGroups = supplierGroups.slice(
        (page - 1) * pageSize,
        page * pageSize
    );

    // Stats
    const stats = useMemo(() => {
        const critical = reorderItems.filter((i) => i.urgency === 'critical').length;
        const low = reorderItems.filter((i) => i.urgency === 'low').length;
        const totalCost = reorderItems.reduce((sum, i) => sum + i.estimatedCost, 0);
        const totalQuantity = reorderItems.reduce((sum, i) => sum + i.reorderQuantity, 0);
        return {
            total: reorderItems.length,
            critical,
            low,
            totalCost,
            totalQuantity,
            supplierCount: supplierGroups.length,
        };
    }, [reorderItems, supplierGroups]);

    // Handlers
    const toggleSupplier = useCallback((supplierId: string) => {
        setExpandedSuppliers((prev) => {
            const next = new Set(prev);
            if (next.has(supplierId)) {
                next.delete(supplierId);
            } else {
                next.add(supplierId);
            }
            return next;
        });
    }, []);

    const expandAll = useCallback(() => {
        setExpandedSuppliers(new Set(paginatedSupplierGroups.map((g) => g.supplierId)));
    }, [paginatedSupplierGroups]);

    const collapseAll = useCallback(() => {
        setExpandedSuppliers(new Set());
    }, []);

    const handleCreateRequisitions = useCallback(() => {
        setShowConfirmModal(true);
    }, []);

    const handlePageChange = useCallback(
        (newPage: number) => {
            if (newPage < 1 || newPage > totalPages || newPage === page) return;
            setPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        [totalPages, page]
    );

    const getBranchName = (id: string) =>
        branchesList.find((b) => b.id === id)?.name || 'Warehouse';

    const formatNaira = (val: number): string => {
        return `₦${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    };

    const getUrgencyBadge = (urgency: ReorderItem['urgency']) => {
        switch (urgency) {
            case 'critical':
                return (
                    <Badge variant="danger" size="sm">
                        Out of Stock
                    </Badge>
                );
            case 'low':
                return (
                    <Badge variant="warning" size="sm">
                        Low Stock
                    </Badge>
                );
            default:
                return (
                    <Badge variant="info" size="sm">
                        Reorder
                    </Badge>
                );
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/inventory')}
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg shadow-amber-500/25">
                                <ShoppingCart className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Reorder Suggestions
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {getBranchName(branchId)} · {stats.total} products need reorder ·{' '}
                                    {stats.supplierCount} suppliers
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() =>
                                    queryClient.invalidateQueries({
                                        queryKey: ['stock-levels-reorder'],
                                    })
                                }
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <RefreshCw className="w-4 h-4" /> Refresh
                            </button>
                            {stats.total > 0 && (
                                <button
                                    onClick={handleCreateRequisitions}
                                    disabled={autoRequisitionMutation.isPending}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:from-amber-600 transition-all font-semibold shadow-lg text-sm disabled:opacity-50"
                                    type="button"
                                >
                                    {autoRequisitionMutation.isPending ? (
                                        <Spinner size="xs" variant="white" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                    Create Requisitions
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                {/* ========== DESCRIPTIVE REORDER STATS ========== */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
                        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 text-center">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-slate-500 to-slate-700 w-fit mx-auto mb-2"><Package className="w-4 h-4 text-white" /></div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider">Need Reorder</p>
                        <p className="text-2xl font-bold text-slate-900">{stats.total.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{stats.supplierCount} suppliers involved</p>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
                        className={clsx('rounded-2xl border p-4 text-center shadow-sm', stats.critical > 0 ? 'bg-rose-50 border-rose-200' : 'bg-white border-slate-200')}>
                        <div className="p-2 rounded-lg bg-gradient-to-br from-rose-500 to-pink-600 w-fit mx-auto mb-2"><XCircle className="w-4 h-4 text-white" /></div>
                        <p className="text-[10px] text-rose-500 uppercase tracking-wider">Out of Stock</p>
                        <p className={clsx('text-2xl font-bold', stats.critical > 0 ? 'text-rose-600' : 'text-slate-900')}>{stats.critical.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{stats.critical > 0 ? 'Urgent — zero stock available' : 'No critical shortages'}</p>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
                        className={clsx('rounded-2xl border p-4 text-center shadow-sm', stats.low > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200')}>
                        <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 w-fit mx-auto mb-2"><AlertTriangle className="w-4 h-4 text-white" /></div>
                        <p className="text-[10px] text-amber-500 uppercase tracking-wider">Low Stock</p>
                        <p className={clsx('text-2xl font-bold', stats.low > 0 ? 'text-amber-600' : 'text-slate-900')}>{stats.low.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{stats.low > 0 ? 'Below 50% of reorder point' : 'No low stock items'}</p>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
                        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 text-center">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 w-fit mx-auto mb-2"><ShoppingCart className="w-4 h-4 text-white" /></div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider">Suggested Order</p>
                        <p className="text-2xl font-bold text-slate-900">{stats.totalQuantity.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Total units recommended</p>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -3 }}
                        className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-4 text-center">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 w-fit mx-auto mb-2"><DollarSign className="w-4 h-4 text-white" /></div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider">Est. Total Cost</p>
                        <p className="text-2xl font-bold text-emerald-700">{formatNaira(stats.totalCost)}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{stats.total > 0 ? `Avg ${formatNaira(stats.totalCost / stats.total)} per product` : 'No products'}</p>
                    </motion.div>
                </div>

                {/* Branch Filter & Search */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                    <div className="flex flex-col lg:flex-row gap-1.5">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by product name, SKU, or supplier..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 text-sm"
                            />
                            {search && (
                                <button
                                    onClick={() => {
                                        setSearch('');
                                        setPage(1);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400 hover:text-slate-600"
                                    type="button"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        {branchesList.length > 1 && (
                            <select
                                value={branchId}
                                onChange={(e) => {
                                    setBranchId(e.target.value);
                                    setPage(1);
                                }}
                                className="px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400"
                            >
                                <option value="">All Branches</option>
                                {branchesList.map((b) => (
                                    <option key={b.id} value={b.id}>
                                        {b.name}
                                    </option>
                                ))}
                            </select>
                        )}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={expandAll}
                                className="px-3 py-2 text-xs font-medium text-slate-600 hover:text-slate-700 bg-slate-100 rounded-lg"
                                type="button"
                            >
                                Expand All
                            </button>
                            <button
                                onClick={collapseAll}
                                className="px-3 py-2 text-xs font-medium text-slate-600 hover:text-slate-700 bg-slate-100 rounded-lg"
                                type="button"
                            >
                                Collapse All
                            </button>
                        </div>
                    </div>
                </div>

                {/* Supplier Groups */}
                {stockLoading ? (
                    <div className="flex justify-center py-20">
                        <Spinner size="xl" />
                    </div>
                ) : paginatedSupplierGroups.length === 0 ? (
                    <EmptyState
                        icon={<Package className="w-16 h-16" />}
                        title="No products need reordering"
                        description={
                            search
                                ? 'No products match your search. Try a different term.'
                                : 'All products are above their reorder points. Great job!'
                        }
                    />
                ) : (
                    <>
                        <div className="space-y-3">
                            {paginatedSupplierGroups.map((group) => {
                                const isExpanded = expandedSuppliers.has(group.supplierId);

                                return (
                                    <Card
                                        key={group.supplierId}
                                        className="overflow-hidden"
                                    >
                                        {/* Supplier Header */}
                                        <div
                                            onClick={() => toggleSupplier(group.supplierId)}
                                            className="p-5 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors bg-gradient-to-r from-slate-50 to-white"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="p-2.5 rounded-xl bg-amber-100">
                                                    <ShoppingCart className="w-5 h-5 text-amber-600" />
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold text-slate-800">
                                                        {group.supplierName}
                                                    </h3>
                                                    <p className="text-xs text-slate-500">
                                                        {group.totalItems} product
                                                        {group.totalItems !== 1 ? 's' : ''}{' '}
                                                        · Est. Cost:{' '}
                                                        {formatNaira(group.totalEstimatedCost)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-4">
                                                <div className="text-right">
                                                    <p className="text-[10px] text-slate-400 uppercase">
                                                        Items
                                                    </p>
                                                    <p className="text-lg font-bold text-slate-700">
                                                        {group.totalItems}
                                                    </p>
                                                </div>
                                                {isExpanded ? (
                                                    <ChevronUp className="w-5 h-5 text-slate-400" />
                                                ) : (
                                                    <ChevronDown className="w-5 h-5 text-slate-400" />
                                                )}
                                            </div>
                                        </div>

                                        {/* Supplier Items */}
                                        <AnimatePresence>
                                            {isExpanded && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="border-t border-slate-100">
                                                        <div className="overflow-x-auto">
                                                            <table className="min-w-full divide-y divide-slate-100">
                                                                <thead>
                                                                    <tr className="bg-slate-50/50">
                                                                        <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase">
                                                                            Product
                                                                        </th>
                                                                        <th className="px-4 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase hidden sm:table-cell">
                                                                            Branch
                                                                        </th>
                                                                        <th className="px-4 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">
                                                                            Current
                                                                        </th>
                                                                        <th className="px-4 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">
                                                                            Reorder Pt
                                                                        </th>
                                                                        <th className="px-4 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">
                                                                            Suggested Qty
                                                                        </th>
                                                                        <th className="px-4 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase hidden md:table-cell">
                                                                            Unit Cost
                                                                        </th>
                                                                        <th className="px-4 py-2 text-right text-[10px] font-semibold text-slate-500 uppercase">
                                                                            Est. Cost
                                                                        </th>
                                                                        <th className="px-4 py-2 text-center text-[10px] font-semibold text-slate-500 uppercase w-24">
                                                                            Status
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-slate-50">
                                                                    {group.items.map((item) => (
                                                                        <tr
                                                                            key={`${item.productId}-${item.branchId}`}
                                                                            className={clsx(
                                                                                'hover:bg-slate-50 transition-colors',
                                                                                item.urgency === 'critical' &&
                                                                                'bg-rose-50/20',
                                                                                item.urgency === 'low' &&
                                                                                'bg-amber-50/20'
                                                                            )}
                                                                        >
                                                                            <td className="px-4 py-2.5">
                                                                                <div className="flex items-center gap-2">
                                                                                    <Package
                                                                                        className={clsx(
                                                                                            'w-4 h-4',
                                                                                            item.urgency ===
                                                                                                'critical'
                                                                                                ? 'text-rose-500'
                                                                                                : item.urgency ===
                                                                                                    'low'
                                                                                                    ? 'text-amber-500'
                                                                                                    : 'text-slate-400'
                                                                                        )}
                                                                                    />
                                                                                    <div>
                                                                                        <p className="text-sm font-medium text-slate-700">
                                                                                            {
                                                                                                item.productName
                                                                                            }
                                                                                        </p>
                                                                                        {item.sku && (
                                                                                            <p className="text-[10px] text-slate-400 font-mono">
                                                                                                {
                                                                                                    item.sku
                                                                                                }
                                                                                            </p>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            </td>
                                                                            <td className="px-4 py-2.5 text-sm text-slate-500 hidden sm:table-cell">
                                                                                {item.branchName}
                                                                            </td>
                                                                            <td className="px-4 py-2.5 text-right">
                                                                                <span
                                                                                    className={clsx(
                                                                                        'text-sm font-bold tabular-nums',
                                                                                        item.urgency ===
                                                                                            'critical'
                                                                                            ? 'text-rose-600'
                                                                                            : item.urgency ===
                                                                                                'low'
                                                                                                ? 'text-amber-600'
                                                                                                : 'text-slate-700'
                                                                                    )}
                                                                                >
                                                                                    {item.currentStock.toLocaleString()}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-4 py-2.5 text-right text-sm text-slate-600 tabular-nums">
                                                                                {item.reorderPoint.toLocaleString()}
                                                                            </td>
                                                                            <td className="px-4 py-2.5 text-right text-sm font-semibold text-blue-600 tabular-nums">
                                                                                {item.reorderQuantity.toLocaleString()}
                                                                            </td>
                                                                            <td className="px-4 py-2.5 text-right text-sm text-slate-600 hidden md:table-cell tabular-nums">
                                                                                {formatNaira(item.costPrice)}
                                                                            </td>
                                                                            <td className="px-4 py-2.5 text-right text-sm font-bold text-slate-700 tabular-nums">
                                                                                {formatNaira(item.estimatedCost)}
                                                                            </td>
                                                                            <td className="px-4 py-2.5 text-center">
                                                                                {getUrgencyBadge(item.urgency)}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={totalSupplierGroups}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setPage(1);
                                }}
                            />
                        )}
                    </>
                )}
            </div>

            {/* Confirmation Modal */}
            <AnimatePresence>
                {showConfirmModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 text-center"
                        >
                            <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
                                <Send className="w-8 h-8 text-amber-600" />
                            </div>
                            <h2 className="text-xl font-bold text-slate-900">
                                Create Purchase Requisitions?
                            </h2>
                            <p className="text-sm text-slate-500 mt-2">
                                This will create purchase requisitions for{' '}
                                <span className="font-bold">{stats.total}</span> products
                                across{' '}
                                <span className="font-bold">{stats.supplierCount}</span>{' '}
                                suppliers.
                            </p>
                            <div className="mt-4 grid grid-cols-2 gap-3">
                                <div className="p-3 bg-slate-50 rounded-xl">
                                    <p className="text-xs text-slate-400">Products</p>
                                    <p className="text-xl font-bold text-slate-700">
                                        {stats.total}
                                    </p>
                                </div>
                                <div className="p-3 bg-slate-50 rounded-xl">
                                    <p className="text-xs text-slate-400">Est. Cost</p>
                                    <p className="text-xl font-bold text-slate-700">
                                        {formatNaira(stats.totalCost)}
                                    </p>
                                </div>
                            </div>
                            <p className="text-xs text-slate-400 mt-3">
                                Products already in pending requisitions will be skipped.
                            </p>
                            <div className="flex gap-3 mt-6">
                                <button
                                    onClick={() => setShowConfirmModal(false)}
                                    className="flex-1 px-5 py-3 border-2 border-slate-300 rounded-xl font-medium text-sm hover:bg-white transition"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => autoRequisitionMutation.mutate()}
                                    disabled={autoRequisitionMutation.isPending}
                                    className="flex-1 px-5 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-semibold text-sm shadow-lg hover:from-amber-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                    type="button"
                                >
                                    {autoRequisitionMutation.isPending ? (
                                        <Spinner size="xs" variant="white" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                    Create Requisitions
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}